import { api } from "services";

import Pager from "components/Pager";
import { IS_DEV } from "globals/constants";
import { modifications as constants } from "store/constants";
import { actionFactory as feedActionFactory } from "store/factories/feed";
import { actionLoaderFactory } from "store/factories/fetchable";
import { dispatch } from "store/store";
import { notifyError } from "utils";

import { modificationListSelectors } from "../selectors";


const factory = actionLoaderFactory(constants, constants.MODIFICATIONS);

export const { setPaging } = feedActionFactory(constants);

export const resetModifications = () => dispatch({ type: constants.RESET });

const updateModificationNumber = async (modificationsData = {}, isRefresh = false) => {
	const prevModificationNumber = modificationListSelectors.content().latestModificationNumber || 0;

	if (modificationsData?.totalItems > modificationsData?.paging?.pageLimit && !isRefresh) {
		// Fetch the first modification that comes back. Modifications come back in descending order by modificationNumber.
		// If this is not the latest modification check to see if there was a change in the BE default order.
		const params = { size: 1, pageLimit: 1 };

		try {
			factory.setIsFetching(true);

			const response = modificationsData?.formInfo.isSubaward ?
				await api.modifications.getSubawardModifications(modificationsData?.formInfo, params) :
				await api.modifications.getAwardModifications(modificationsData?.formInfo, params);

			factory.setData({
				...modificationsData,
				latestModificationNumber: response.content[0].modificationNumber,
			});
		} catch (error) {
			console.error(error);
			const message = IS_DEV ? error.message : "An error has occurred";
			notifyError(message, error);
		}
	} else if (!isRefresh) {
		const greatestValue = modificationsData?.items?.reduce((acc, value) => {
			return acc > value.modificationNumber ? acc : value.modificationNumber;
		}, 0);

		factory.setData({
			...modificationsData,
			latestModificationNumber: greatestValue,
		});
	} else {
		factory.setData({
			...modificationsData,
			latestModificationNumber: prevModificationNumber,
		});
	}

	factory.setIsFetching(false);
};

export const refreshFormModifications = async () => {
	const {
		formInfo,
		paging: {
			pageLimit,
			pageNumber,
		},
	} = modificationListSelectors.content();

	if (!formInfo) {
		return;
	}

	const pageOffset = Pager.pageOffsetByPageNumber(pageNumber);
	const params = { size: pageLimit, page: pageOffset };

	const response = formInfo.isSubaward ?
		await api.modifications.getSubawardModifications(formInfo, params) :
		await api.modifications.getAwardModifications(formInfo, params);

	factory.setData({
		items: response.content,
		totalItems: response.totalElements,
	});

	return response;
};

export const getFormModifications = async ({ formInfo, params = {} }) => {
	try {
		factory.setIsFetching(true);

		const response = formInfo.isSubaward ?
			await api.modifications.getSubawardModifications(formInfo, params) :
			await api.modifications.getAwardModifications(formInfo, params);

		const modificationsData = {
			formInfo,
			items: response.content,
			paging: {
				pageLimit: params?.size ?? 10,
				pageNumber: params?.page ?? 1,
			},
			totalItems: response.totalElements,
		};

		factory.setData(modificationsData);

		await updateModificationNumber(modificationsData);

		return response;
	} catch (error) {
		const message = IS_DEV ? error.message : "An error has occurred";

		notifyError(message, error);
		factory.setError(message);
		factory.setIsFetching(false);
	}
};

export const cancelModification = async (formInfo, url) => {
	await factory.createLoaderAction(
		() => api.modifications.cancel(url),
		[
			() => {
				updateModificationNumber();
				setTimeout(() => getFormModifications({ formInfo }), 500);
			},
			message => {
				notifyError(message);
				factory.setError(message);
			},
		],
	);
};

export const continueModification = async (formInfo, url, reason) => {
	return factory.createLoaderAction(
		() => api.modifications.update(url, { modificationType: reason }),
		[
			() => {
				updateModificationNumber();
				getFormModifications({ formInfo });
			},
			message => {
				notifyError(message);
				factory.setError(message);
			},
		],
	);
};

export const completeModification = async (formInfo, url, reason, comment, reroute) => {
	await factory.createLoaderAction(
		() => (reroute
			? api.modifications.completeAndReRoute(url, { modificationType: reason, comment })
			: api.modifications.complete(url, { modificationType: reason, comment })),
		[
			() => {
				updateModificationNumber();
				setTimeout(() => getFormModifications({ formInfo }), 1000);
			},
			message => {
				notifyError(message);
				factory.setError(message);
			},
		],
	);
};
